import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Surface } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Pencil, Trash2, HardHat, Upload, FileText, Eye, EyeOff, User, Wrench } from "lucide-react";
import { toast } from "sonner";
import { getUser } from "@/lib/api";

const isAdmin = () => {
  const u = getUser();
  return u && (u.role || "").toLowerCase().includes("admin");
};

export default function Workers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [chargesFor, setChargesFor] = useState(null);   // {id, name} → open worker×category charges modal
  const [showDocs, setShowDocs] = useState(false);   // admin toggle to reveal document urls
  const admin = isAdmin();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (typeFilter !== "all") params.worker_type = typeFilter;
      if (admin && showDocs) params.include_documents = true;
      const res = await api.get("/production/workers", { params });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  }, [search, typeFilter, showDocs, admin]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (w) => {
    if (!window.confirm(`Deactivate worker "${w.name}"?`)) return;
    try {
      await api.delete(`/production/workers/${w.id}`);
      toast.success("Worker deactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input placeholder="Search by name or mobile…" className="pl-8 h-9 text-sm bg-white" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="wk-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-52 h-9 text-sm" data-testid="wk-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Worker Types</SelectItem>
            <SelectItem value="Commission">Commission</SelectItem>
            <SelectItem value="Permanent">Permanent</SelectItem>
          </SelectContent>
        </Select>
        {admin && (
          <button
            type="button"
            onClick={() => setShowDocs((v) => !v)}
            className={`h-9 px-3 rounded-md border text-xs font-semibold flex items-center gap-1.5 transition-all ${
              showDocs ? "bg-gray-900 border-gray-900 text-white" : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
            }`}
            title="Toggle visibility of Aadhar/Voter documents (admin only)"
            data-testid="wk-show-docs"
          >
            {showDocs ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {showDocs ? "Hide IDs" : "Show IDs"}
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">{items.length} worker{items.length !== 1 ? "s" : ""}</span>
          <Button className="h-9 bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={() => setEditing({ __new: true, name: "", mobile: "", address: "", area: "", city: "", district: "", state: "", photo_url: null, id_document_url: null, worker_type: "Commission" })} data-testid="wk-add-btn">
            <Plus className="h-4 w-4" /> Add Worker
          </Button>
        </div>
      </div>

      <Surface padded={false}>
        <div className="overflow-auto max-h-[calc(100vh-200px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2.5 w-12">Photo</th>
                <th className="px-3 py-2.5">Name</th>
                <th className="px-3 py-2.5">Mobile</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Area</th>
                <th className="px-3 py-2.5">City / District</th>
                <th className="px-3 py-2.5">State</th>
                <th className="px-3 py-2.5">ID Doc</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={9} className="text-center py-8 text-gray-400">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400">
                  <HardHat className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No workers yet — click <span className="font-semibold text-gray-700">Add Worker</span>
                </td></tr>
              )}
              {items.map((w) => (
                <tr key={w.id} className="hover:bg-gray-50" data-testid={`wk-row-${w.id}`}>
                  <td className="px-3 py-2">
                    {w.photo_url ? (
                      <img src={w.photo_url} alt={w.name} className="h-8 w-8 rounded-full object-cover border border-gray-200" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                        <User className="h-4 w-4" />
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900">{w.name}</td>
                  <td className="px-3 py-2 text-gray-600">{w.mobile || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${
                      w.worker_type === "Commission" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"
                    }`}>{w.worker_type}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{w.area || "—"}</td>
                  <td className="px-3 py-2 text-gray-600">{[w.city, w.district].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-3 py-2 text-gray-600">{w.state || "—"}</td>
                  <td className="px-3 py-2">
                    {w.id_document_url ? (
                      <a href={w.id_document_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#9B1C1C] hover:underline">
                        <FileText className="h-3.5 w-3.5" /> View
                      </a>
                    ) : w.has_id_document ? (
                      <span className="text-[11px] text-gray-400 italic">Uploaded (admin only)</span>
                    ) : (
                      <span className="text-[11px] text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setChargesFor({ id: w.id, name: w.name })} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-gray-100 text-xs text-gray-700" data-testid={`wk-charges-${w.id}`}>
                      <Wrench className="h-3.5 w-3.5" /> Charges
                    </button>
                    <button onClick={() => setEditing({ ...w })} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-gray-100 text-xs text-gray-700" data-testid={`wk-edit-${w.id}`}>
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                    <button onClick={() => remove(w)} className="inline-flex items-center gap-1 h-7 px-2 rounded hover:bg-red-50 text-xs text-red-700" data-testid={`wk-delete-${w.id}`}>
                      <Trash2 className="h-3.5 w-3.5" /> Deactivate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      {editing && (
        <WorkerEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {chargesFor && (
        <WorkerCategoryChargesDialog
          worker={chargesFor}
          onClose={() => setChargesFor(null)}
        />
      )}
    </div>
  );
}

function WorkerEditor({ initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [form, setForm] = useState({
    name: initial.name || "",
    mobile: initial.mobile || "",
    address: initial.address || "",
    area: initial.area || "",
    city: initial.city || "",
    district: initial.district || "",
    state: initial.state || "",
    photo_url: initial.photo_url || null,
    id_document_url: initial.id_document_url || null,
    worker_type: initial.worker_type || "Commission",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");   // "photo" | "id_document" | ""
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (kind, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    setUploading(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/production/workers/upload?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set(kind === "photo" ? "photo_url" : "id_document_url", res.data.url);
      toast.success(`${kind === "photo" ? "Photo" : "ID document"} uploaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading("");
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/production/workers", form);
        toast.success("Worker created");
      } else {
        await api.put(`/production/workers/${initial.id}`, form);
        toast.success("Worker updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isNew ? "New Worker" : `Edit — ${initial.name}`}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-1 space-y-3">
            <PhotoBox
              label="Photo"
              url={form.photo_url}
              busy={uploading === "photo"}
              onSelect={(f) => upload("photo", f)}
              onClear={() => set("photo_url", null)}
              testId="wk-photo"
              placeholder={<User className="h-10 w-10 text-gray-300" />}
            />
            <PhotoBox
              label="Aadhar / Voter ID"
              url={form.id_document_url}
              busy={uploading === "id_document"}
              onSelect={(f) => upload("id_document", f)}
              onClear={() => set("id_document_url", null)}
              testId="wk-id-doc"
              placeholder={<FileText className="h-10 w-10 text-gray-300" />}
              hint="Admin-only visibility"
            />
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <Field label="Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="wk-name" /></Field>
            <Field label="Worker Type *">
              <Select value={form.worker_type} onValueChange={(v) => set("worker_type", v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="wk-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Commission">Commission</SelectItem>
                  <SelectItem value="Permanent">Permanent</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Mobile"><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="wk-mobile" /></Field>
            <Field label="Area"><Input value={form.area} onChange={(e) => set("area", e.target.value)} data-testid="wk-area" /></Field>
            <Field label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="wk-city" /></Field>
            <Field label="District"><Input value={form.district} onChange={(e) => set("district", e.target.value)} data-testid="wk-district" /></Field>
            <Field label="State" className="col-span-2"><Input value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="wk-state" /></Field>
            <Field label="Address" className="col-span-2">
              <textarea className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 min-h-[70px]" value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="wk-address" />
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D]" onClick={save} disabled={saving} data-testid="wk-save">
            {saving ? "Saving…" : (isNew ? "Create Worker" : "Save Changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WorkerCategoryChargesDialog({ worker, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/production/workers/${worker.id}/category-charges`);
      setRows(r.data?.items || []);
      setDirty({});
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [worker.id]);
  useEffect(() => { load(); }, [load]);

  const dirtyCount = Object.keys(dirty).length;
  const saveOne = async (cat, val) => {
    try {
      await api.put(`/production/workers/${worker.id}/category-charges/${encodeURIComponent(cat)}`, { worker_charge: val === "" ? null : Number(val) });
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); throw e; }
  };
  const saveAll = async () => {
    setSavingAll(true);
    let ok = 0, fail = 0;
    for (const [cat, val] of Object.entries(dirty)) {
      try { await saveOne(cat, val); ok++; } catch { fail++; }
    }
    toast[fail ? "error" : "success"](`Saved ${ok}${fail ? ` · ${fail} failed` : ""}`);
    setSavingAll(false);
    load();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench className="h-4 w-4 text-[#9B1C1C]" /> Worker Charges — {worker.name}</DialogTitle>
          <p className="text-xs text-gray-500">Per-category rate for this worker. Leave blank to fall back to the global category default. Applied automatically when receiving an L1 or L2 batch.</p>
        </DialogHeader>
        <div className="overflow-auto -mx-6 px-6">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2 text-right w-40">Global Default</th>
                <th className="px-3 py-2 text-right w-52">This Worker&apos;s Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={3} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={3} className="px-3 py-8 text-center text-gray-400">No categories found. Add categories on your raw materials first.</td></tr>}
              {rows.map((r) => {
                const val = dirty[r.category] !== undefined ? dirty[r.category] : (r.worker_charge ?? "");
                return (
                  <tr key={r.category} className={dirty[r.category] !== undefined ? "bg-amber-50/40" : ""}>
                    <td className="px-3 py-2 font-medium">{r.category}</td>
                    <td className="px-3 py-2 text-right text-xs text-gray-500 tabular-nums">{r.category_default != null ? r.category_default : "—"}</td>
                    <td className="px-3 py-2">
                      <Input
                        type="number" step="0.01" value={val}
                        onChange={(e) => setDirty((d) => ({ ...d, [r.category]: e.target.value }))}
                        placeholder={r.category_default != null ? `(default ${r.category_default})` : "blank = 0"}
                        className="text-right tabular-nums h-8"
                        data-testid={`wcc-input-${r.category}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D]" onClick={saveAll} disabled={!dirtyCount || savingAll} data-testid="wcc-save-all">
            {savingAll ? "Saving…" : dirtyCount ? `Save ${dirtyCount} change${dirtyCount === 1 ? "" : "s"}` : "No changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoBox({ label, url, busy, onSelect, onClear, testId, placeholder, hint }) {
  const inputRef = useRef(null);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">{label}</span>
        {url && (
          <button type="button" onClick={onClear} className="text-[10px] text-red-600 hover:underline" data-testid={`${testId}-clear`}>
            Remove
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-full aspect-square rounded-md border-2 border-dashed border-gray-200 hover:border-[#9B1C1C] bg-gray-50 flex flex-col items-center justify-center overflow-hidden transition-colors relative"
        data-testid={testId}
      >
        {url ? (
          <img src={url} alt={label} className="w-full h-full object-cover" />
        ) : (
          <>
            {placeholder}
            <span className="mt-2 text-[11px] text-gray-500 flex items-center gap-1">
              <Upload className="h-3 w-3" /> Upload
            </span>
          </>
        )}
        {busy && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <span className="text-xs text-gray-600">Uploading…</span>
          </div>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => onSelect(e.target.files?.[0])}
        data-testid={`${testId}-input`}
      />
      {hint && <p className="text-[10px] text-gray-400 italic">{hint}</p>}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-1">{label}</span>
      {children}
    </label>
  );
}
